import {
  Modal,
  Table,
  Tag,
  Spin,
  Badge,
  Flex,
  Button,
  Tooltip,
  Row,
  Col,
  Typography,
  Space,
} from "antd";
import {
  CloudSyncOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  SyncOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleFilled,
  PauseCircleFilled,
} from "@ant-design/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import SummaryCountModal from "./SummaryCountModal";
import {
  getEchsG7SyncReadiness,
  getPortalDetailsByTypeAndId,
  retrySummaryDataFetchEODBOD,
  syncEchsG7AuditRemarks,
  syncEchsG7ClaimPending,
  syncEchsG7PreauthPending,
  syncEchsG7Query,
  syncEchsG7SettlementPending,
  triggerAuditRemarksSummaryDataFetchEODBOD,
  triggerAuditTrailsSummaryDataFetchEODBOD,
} from "../../../apis/reconciliationService";
import { errorMessage, successMessage } from "../../../utils/notificationUtils";

const { Text, Title } = Typography;

const BRAND = "#00549D";
const BRAND_SOFT = "rgba(0, 84, 157, 0.08)";
const TRIGGER_ROW_TYPES = [
  "echs_disallowance",
  "echs_portal_settlement_audit_remarks",
  "echs_settlement_audit_remarks",
  "echs_query_remarks",
  "echs_portal_query_audit_trail_batch",
  "echs_query_audit_trail",
  "echs_query_audit_trails",
];
const PAGE_BG =
  "linear-gradient(165deg, #f4f7fb 0%, #eef3f9 42%, #f8fafc 100%)";

interface TileData {
  batchId: number;
  id: number;
  recordsFetched: number;
  retryAt: string | null;
  retryBy: string | null;
  status?: string;
  type: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  summaryData: any;
  tilesData: TileData[];
  summaryDataTotal: number;
  isSummaryModalDataLoading: boolean;
  onPaginationChange: (page: number, size: number) => void;
}

const formatSyncAt = (value?: string | null) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY, HH:mm") : value;
};

const SummaryModal: React.FC<Props> = ({
  open,
  onClose,
  summaryData,
  tilesData,
  summaryDataTotal,
  isSummaryModalDataLoading,
  onPaginationChange,
}) => {
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedTile, setSelectedTile] = useState<any>(null);
  const [selectedTileIndex, setSelectedTileIndex] = useState<number | null>(
    null,
  );
  const [countModalData, setCountModalData] = useState<any>([]);
  const [countModalTotal, setCountModalTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
  });
  const [g7Readiness, setG7Readiness] = useState<any>(null);
  const [g7Loading, setG7Loading] = useState(false);
  const [g7SyncingKey, setG7SyncingKey] = useState<string | null>(null);

  const isECHS = !!summaryData?.scheme?.toUpperCase()?.includes("ECHS");

  const loadG7Readiness = useCallback(async () => {
    if (
      !summaryData?.scheme?.toUpperCase()?.includes("ECHS") ||
      !summaryData?.hospitalId ||
      !summaryData?.schemeId
    ) {
      setG7Readiness(null);
      return;
    }
    try {
      setG7Loading(true);
      const res = await getEchsG7SyncReadiness(
        summaryData.hospitalId,
        summaryData.schemeId,
        summaryData.eodBodFetchActivityId,
      );
      setG7Readiness(res);
    } catch (err: any) {
      setG7Readiness(null);
      errorMessage(
        err?.response?.data?.message || "Failed to load G7 sync readiness",
      );
    } finally {
      setG7Loading(false);
    }
  }, [
    summaryData?.hospitalId,
    summaryData?.schemeId,
    summaryData?.scheme,
    summaryData?.eodBodFetchActivityId,
  ]);

  useEffect(() => {
    if (open && isECHS) {
      loadG7Readiness();
    }
  }, [open, isECHS, loadG7Readiness]);

  const handleG7Sync = async (
    key: string,
    api: (payload: any) => Promise<any>,
  ) => {
    if (!summaryData?.hospitalId || !summaryData?.schemeId) {
      errorMessage("Missing hospital or scheme for G7 sync.");
      return;
    }
    try {
      setG7SyncingKey(key);
      const res = await api({
        hospitalId: summaryData.hospitalId,
        schemeId: summaryData.schemeId,
        eodBodFetchActivityId: summaryData.eodBodFetchActivityId || null,
        orchestratorId: summaryData.orchestratorId || null,
      });
      const who = res?.syncedBy ? ` by ${res.syncedBy}` : "";
      successMessage(
        res?.message
          ? `${res.message} (${res.synced ?? 0} records${who})`
          : "G7 sync completed",
      );
      await loadG7Readiness();
    } catch (err: any) {
      errorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "G7 sync failed. Please try again later.",
      );
    } finally {
      setG7SyncingKey(null);
    }
  };

  const getPortalDetails = async (
    type: string,
    id: number,
    pageNumber: number = 1,
    pageSize: number = 100,
    searchQuery?: string,
  ) => {
    try {
      setLoading(true);
      const page = pageNumber - 1;
      const size = pageSize;

      const res = await getPortalDetailsByTypeAndId(
        type,
        id,
        summaryData.hospitalId,
        summaryData.schemeId,
        page,
        size,
        searchQuery?.trim(),
      );

      setCountModalData(res?.content || []);
      setCountModalTotal(res?.totalElements || 0);
      setLoading(false);
    } catch (err) {
      errorMessage("Failed to fetch detailed data. Please try again later.");
      setLoading(false);
      setCountModalData([]);
    }
  };

  const handleTileClick = async (tile: TileData, index: number) => {
    setSelectedTile(tile);
    setSelectedTileIndex(index);
    setOpenDetailModal(true);
    getPortalDetails(tile.type, tile.id);
  };

  const resetTileSelection = () => {
    setSelectedTile(null);
    setSelectedTileIndex(null);
  };

  useEffect(() => {
    if (!open) {
      resetTileSelection();
      setCountModalData([]);
      setCountModalTotal(0);
      setPagination({
        current: 1,
        pageSize: 100,
      });
    }
  }, [open]);

  useEffect(() => {
    if (!openDetailModal) {
      setCountModalData([]);
      setCountModalTotal(0);
    }
  }, [openDetailModal]);

  const getStatusColor = (status?: string) => {
    const normalized = status?.toUpperCase();
    if (normalized === "SUCCESS") return "green";
    if (normalized === "FAILED" || normalized === "FAILURE") return "red";
    return "orange";
  };

  const handleRetry = async (record: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!summaryData?.orchestratorId) {
      errorMessage("Missing orchestrator details for retry.");
      return;
    }
    const recordType = (record?.type || "").toLowerCase();
    const type =
      recordType === "echs_review_request"
        ? "arbitration"
        : recordType.replace(/^echs_/, "");
    try {
      const res = await retrySummaryDataFetchEODBOD({
        batchId: record?.batchId || null,
        type,
        orchestratorId: summaryData.orchestratorId,
      });
      successMessage(res?.message || "Retry initiated successfully");
    } catch (err: any) {
      errorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to retry. Please try again later.",
      );
    }
  };

  const handleTrigger = async (record: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!summaryData?.hospitalId || !summaryData?.schemeId) {
      errorMessage("Missing hospital or scheme details for trigger.");
      return;
    }
    if (!summaryData?.orchestratorId) {
      errorMessage("Missing orchestrator details for trigger.");
      return;
    }
    const recordType = (record?.type || "").toLowerCase();
    const payload = {
      hospitalId: summaryData.hospitalId,
      schemeId: summaryData.schemeId,
      orchestratorId: summaryData.orchestratorId,
    };
    try {
      let res;
      if (
        recordType === "echs_disallowance" ||
        recordType === "echs_portal_settlement_audit_remarks" ||
        recordType === "echs_settlement_audit_remarks"
      ) {
        res = await triggerAuditRemarksSummaryDataFetchEODBOD(payload);
      } else if (
        recordType === "echs_query_remarks" ||
        recordType === "echs_portal_query_audit_trail_batch" ||
        recordType === "echs_query_audit_trail" ||
        recordType === "echs_query_audit_trails"
      ) {
        res = await triggerAuditTrailsSummaryDataFetchEODBOD(payload);
      } else {
        return;
      }
      successMessage(res?.message || "Trigger initiated successfully");
    } catch (err: any) {
      errorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to trigger. Please try again later.",
      );
    }
  };

  const g7SyncRows = [
    {
      key: "preauthPending",
      label: "Preauth Pending",
      hint: "Patient referral + admission intimation pending → G7",
      enabled: !!g7Readiness?.preauthPending?.enabled,
      reason: g7Readiness?.preauthPending?.reason,
      lastSync: g7Readiness?.preauthPending?.lastSync,
      api: syncEchsG7PreauthPending,
    },
    {
      key: "claimPending",
      label: "Claim Pending",
      hint: "Status Excel legs → G7 claim pending",
      enabled: !!g7Readiness?.claimPending?.enabled,
      reason: g7Readiness?.claimPending?.reason,
      lastSync: g7Readiness?.claimPending?.lastSync,
      api: syncEchsG7ClaimPending,
    },
    {
      key: "settlementPending",
      label: "Settlement Pending",
      hint: "Unsettled G7 cases matched to portal settlement",
      enabled: !!g7Readiness?.settlementPending?.enabled,
      reason: g7Readiness?.settlementPending?.reason,
      lastSync: g7Readiness?.settlementPending?.lastSync,
      api: syncEchsG7SettlementPending,
    },
    {
      key: "auditRemarks",
      label: "Disallowance Sync",
      hint: "Portal Overall Detailed Summary → G7 disallowance",
      enabled: !!g7Readiness?.auditRemarks?.enabled,
      reason: g7Readiness?.auditRemarks?.reason,
      lastSync: g7Readiness?.auditRemarks?.lastSync,
      api: syncEchsG7AuditRemarks,
    },
    {
      key: "query",
      label: "Query Sync",
      hint: "NMI status + audit trails → G7 case preauth queries",
      enabled: !!g7Readiness?.query?.enabled,
      reason: g7Readiness?.query?.reason,
      lastSync: g7Readiness?.query?.lastSync,
      api: syncEchsG7Query,
    },
  ];

  const successCount = tilesData.filter(
    (t) => (t.status || "").toUpperCase() === "SUCCESS",
  ).length;
  const runningCount = tilesData.filter((t) => {
    const s = (t.status || "").toUpperCase();
    return s === "RUNNING" || s === "STARTED" || s === "PROCESSING";
  }).length;
  const failedCount = tilesData.filter((t) => {
    const s = (t.status || "").toUpperCase();
    return s === "FAILED" || s === "FAILURE";
  }).length;

  const columns = [
    {
      title: "#",
      dataIndex: "sno",
      width: isECHS ? 56 : "20%",
      align: "center" as const,
      render: (_: any, __: any, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Data fetch",
      dataIndex: "type",
      width: isECHS ? undefined : "30%",
      ellipsis: true,
      render: (value: any) => (
        <Text style={{ fontWeight: 500, color: "#1e293b" }}>
          {value ? value.replaceAll("_", " ") : "-"}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: isECHS ? 130 : "25%",
      align: "center" as const,
      render: (status: string) => (
        <Tag
          color={getStatusColor(status)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            borderRadius: 999,
            padding: "2px 10px",
            margin: 0,
            fontWeight: 600,
            letterSpacing: 0.2,
          }}
        >
          <Badge color={getStatusColor(status)} />
          {status ?? "-"}
        </Tag>
      ),
    },
    {
      title: "Records",
      dataIndex: "recordsFetched",
      width: isECHS ? 100 : "25%",
      align: "center" as const,
      render: (value: any) => (
        <Text style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
          {value !== undefined && value !== null ? value.toLocaleString() : "-"}
        </Text>
      ),
    },
    ...(isECHS
      ? [
          {
            title: "Actions",
            dataIndex: "action",
            width: 90,
            align: "center" as const,
            render: (_: any, record: any) => {
              const recordType = (record?.type || "").toLowerCase();
              const showTrigger = TRIGGER_ROW_TYPES.includes(recordType);
              const retryEnabled = record?.recordsFetched === 0;

              return (
                <Button
                  type="link"
                  size="small"
                  disabled={!showTrigger && !retryEnabled}
                  style={{
                    color: showTrigger || retryEnabled ? BRAND : undefined,
                    fontWeight: 600,
                    padding: 0,
                  }}
                  onClick={(e) =>
                    showTrigger
                      ? handleTrigger(record, e)
                      : handleRetry(record, e)
                  }
                >
                  {showTrigger ? "Trigger" : "Retry"}
                </Button>
              );
            },
          },
          {
            title: "Retry by",
            dataIndex: "retryBy",
            width: 110,
            ellipsis: true,
            render: (value: any) =>
              value !== undefined && value !== null ? value : "—",
          },
          {
            title: "Retry at",
            dataIndex: "retryAt",
            width: 140,
            ellipsis: true,
            render: (value: any) =>
              value !== undefined && value !== null
                ? formatSyncAt(value) || value
                : "—",
          },
        ]
      : []),
  ];

  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(360);

  // Measure the card body so Ant Table scroll.y fits and the last rows (e.g. audit trail) stay reachable.
  useEffect(() => {
    if (!open) return;
    const el = tableWrapRef.current;
    if (!el) return;
    const update = () => {
      // Table header ~55px + pagination ~64px + padding buffer
      const next = Math.floor(el.clientHeight - 130);
      setScrollY(Math.max(180, next));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [open, isECHS, tilesData.length]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={"100vw"}
      title={null}
      closable
      wrapClassName="management-age-stage-table-modal echs-summary-modal"
      bodyStyle={{
        // 144vh pairs with global scale(0.67) on .management-age-stage-table-modal
        height: "calc(144vh - 55px)",
        overflow: "hidden",
        padding: 0,
        background: PAGE_BG,
        display: "flex",
        flexDirection: "column",
      }}
      style={{
        top: 10,
        left: 4,
        padding: 0,
        maxWidth: "100vw",
        height: "145vh",
      }}
    >
      <div
        style={{
          padding: "18px 22px 14px",
          borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
          background:
            "linear-gradient(90deg, rgba(0,84,157,0.06) 0%, transparent 55%)",
          flexShrink: 0,
        }}
      >
        <Flex justify="space-between" align="center" gap={16} wrap="wrap">
          <Space size={14} align="center">
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: BRAND,
                display: "grid",
                placeItems: "center",
                color: "#fff",
                boxShadow: "0 8px 20px rgba(0,84,157,0.28)",
              }}
            >
              <DatabaseOutlined style={{ fontSize: 18 }} />
            </div>
            <div>
              <Title
                level={4}
                style={{ margin: 0, color: "#0f172a", letterSpacing: -0.3 }}
              >
                Portal summary
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {summaryData?.scheme || "Scheme"}
                {summaryData?.eodBodFetchActivityId
                  ? ` · Activity #${summaryData.eodBodFetchActivityId}`
                  : ""}
              </Text>
            </div>
          </Space>
          <Space size={8} wrap>
            {isECHS && (
              <>
                <Tag
                  icon={<CheckCircleFilled />}
                  color="success"
                  style={{ borderRadius: 999, margin: 0 }}
                >
                  {successCount} success
                </Tag>
                <Tag
                  icon={<SyncOutlined spin={runningCount > 0} />}
                  color="warning"
                  style={{ borderRadius: 999, margin: 0 }}
                >
                  {runningCount} running
                </Tag>
                <Tag
                  icon={<PauseCircleFilled />}
                  color="error"
                  style={{ borderRadius: 999, margin: 0 }}
                >
                  {failedCount} failed
                </Tag>
              </>
            )}
            <Tooltip title="Refresh data">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  onPaginationChange(pagination.current, pagination.pageSize);
                  if (isECHS) loadG7Readiness();
                }}
                loading={isSummaryModalDataLoading || g7Loading}
                style={{
                  borderRadius: 10,
                  fontWeight: 600,
                  color: BRAND,
                  borderColor: "rgba(0,84,157,0.25)",
                }}
              >
                Refresh
              </Button>
            </Tooltip>
          </Space>
        </Flex>
      </div>

      <Row gutter={0} style={{ flex: 1, minHeight: 0 }}>
        <Col
          span={isECHS ? 14 : 24}
          style={{ padding: "16px 18px 20px", height: "100%", minHeight: 0 }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: "1px solid rgba(15,23,42,0.06)",
              boxShadow: "0 12px 32px rgba(15,23,42,0.04)",
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Flex
              align="center"
              gap={8}
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid #f1f5f9",
                flexShrink: 0,
              }}
            >
              <DatabaseOutlined style={{ color: BRAND }} />
              <Text strong style={{ fontSize: 15, color: "#0f172a" }}>
                Data fetch
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Portal legs for this activity
              </Text>
            </Flex>
            <div
              ref={tableWrapRef}
              style={{
                padding: "8px 10px 12px",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Spin
                spinning={isSummaryModalDataLoading}
                tip="Loading summary..."
                style={{ flex: 1, minHeight: 0 }}
              >
                <Table
                  columns={columns}
                  dataSource={tilesData}
                  rowKey={(record: any, index) =>
                    `${record?.type}-${record?.id}-${index}`
                  }
                  tableLayout="fixed"
                  className={
                    isECHS
                      ? "echs-summary-fetch-table"
                      : "echs-summary-fetch-table scheme-summary-fetch-table"
                  }
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: summaryDataTotal,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: (page, pageSize) => {
                      setPagination({ current: page, pageSize });
                      onPaginationChange(page, pageSize);
                    },
                  }}
                  scroll={{
                    // ECHS sits beside G7, so a fixed min width keeps those extra columns readable.
                    // Other schemes use the full card, so the table should stretch to 100%.
                    x: isECHS ? 720 : "100%",
                    y: scrollY,
                  }}
                  size="middle"
                  rowClassName={(record) =>
                    TRIGGER_ROW_TYPES.includes(
                      (record?.type || "").toLowerCase(),
                    )
                      ? "echs-summary-row echs-summary-trigger-row"
                      : "echs-summary-row"
                  }
                  onRow={(record, index) => ({
                    onClick: () => handleTileClick(record, index ?? 0),
                    style: {
                      cursor: "pointer",
                      background:
                        selectedTileIndex === index ? BRAND_SOFT : undefined,
                    },
                  })}
                />
              </Spin>
            </div>
          </div>
        </Col>

        {isECHS && (
          <Col
            span={10}
            style={{
              padding: "16px 18px 20px 0",
              height: "100%",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                border: "1px solid rgba(15,23,42,0.06)",
                boxShadow: "0 12px 32px rgba(15,23,42,0.04)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Flex
                justify="space-between"
                align="center"
                style={{
                  padding: "14px 18px",
                  borderBottom: "1px solid #f1f5f9",
                  background:
                    "linear-gradient(135deg, rgba(0,84,157,0.07), transparent)",
                }}
              >
                <Space size={8}>
                  <CloudSyncOutlined style={{ color: BRAND, fontSize: 16 }} />
                  <div>
                    <Text strong style={{ fontSize: 15, color: "#0f172a" }}>
                      G7 sync
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Push portal data into G7 connectors
                      </Text>
                    </div>
                  </div>
                </Space>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={loadG7Readiness}
                  loading={g7Loading}
                  style={{ borderRadius: 8 }}
                >
                  Refresh
                </Button>
              </Flex>

              <div
                style={{
                  padding: 14,
                  overflow: "auto",
                  flex: 1,
                }}
              >
                <Spin spinning={g7Loading}>
                  <Flex vertical gap={10}>
                    {g7SyncRows.map((row) => {
                      const last = row.lastSync;
                      const syncedAt = formatSyncAt(last?.syncedAt);
                      return (
                        <div
                          key={row.key}
                          style={{
                            borderRadius: 14,
                            border: row.enabled
                              ? "1px solid rgba(34,197,94,0.35)"
                              : "1px solid #e2e8f0",
                            background: row.enabled
                              ? "linear-gradient(135deg, #f0fdf4 0%, #ffffff 70%)"
                              : "#f8fafc",
                            padding: "12px 14px",
                            transition: "border-color 0.2s ease",
                          }}
                        >
                          <Flex
                            justify="space-between"
                            align="flex-start"
                            gap={12}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Flex align="center" gap={8} wrap="wrap">
                                <Text
                                  strong
                                  style={{ color: "#0f172a", fontSize: 14 }}
                                >
                                  {row.label}
                                </Text>
                                <Tag
                                  color={row.enabled ? "success" : "default"}
                                  style={{
                                    borderRadius: 999,
                                    margin: 0,
                                    fontSize: 11,
                                  }}
                                >
                                  {row.enabled ? "Ready" : "Blocked"}
                                </Tag>
                              </Flex>
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                  display: "block",
                                  marginTop: 4,
                                }}
                              >
                                {row.enabled
                                  ? row.hint
                                  : row.reason || "Not ready"}
                              </Text>

                              <div
                                style={{
                                  marginTop: 10,
                                  padding: "8px 10px",
                                  borderRadius: 10,
                                  background: "rgba(15,23,42,0.03)",
                                  border: "1px dashed #e2e8f0",
                                }}
                              >
                                {last ? (
                                  <Space
                                    direction="vertical"
                                    size={2}
                                    style={{ width: "100%" }}
                                  >
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        color: "#334155",
                                      }}
                                    >
                                      <UserOutlined
                                        style={{ marginRight: 6, color: BRAND }}
                                      />
                                      {last.syncedByName ||
                                        (last.syncedBy
                                          ? `User #${last.syncedBy}`
                                          : "Unknown user")}
                                    </Text>
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        color: "#334155",
                                      }}
                                    >
                                      <ClockCircleOutlined
                                        style={{ marginRight: 6, color: BRAND }}
                                      />
                                      {syncedAt || "—"}
                                    </Text>
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        color: "#334155",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {Number(
                                        last.recordsSynced ?? 0,
                                      ).toLocaleString()}{" "}
                                      records synced
                                      {last.status
                                        ? ` · ${last.status}`
                                        : ""}
                                    </Text>
                                  </Space>
                                ) : (
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12 }}
                                  >
                                    Not synced yet for this hospital / scheme
                                  </Text>
                                )}
                              </div>
                            </div>
                            <Button
                              type="primary"
                              disabled={!row.enabled || !row.api}
                              loading={g7SyncingKey === row.key}
                              icon={<SyncOutlined />}
                              onClick={() =>
                                row.api && handleG7Sync(row.key, row.api)
                              }
                              style={{
                                background: row.enabled ? BRAND : undefined,
                                borderRadius: 10,
                                fontWeight: 600,
                                minWidth: 88,
                              }}
                            >
                              Sync
                            </Button>
                          </Flex>
                        </div>
                      );
                    })}
                  </Flex>
                </Spin>
              </div>
            </div>
          </Col>
        )}
      </Row>

      <SummaryCountModal
        open={openDetailModal}
        onClose={() => setOpenDetailModal(false)}
        scheme={summaryData?.scheme}
        countModalData={countModalData}
        isLoading={loading}
        selectedTile={selectedTile}
        countModalTotal={countModalTotal}
        summaryData={summaryData}
        getPortalDetails={getPortalDetails}
      />
    </Modal>
  );
};

export default SummaryModal;
